import { useState } from 'react';
import { ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

// UI Components
import { Box, Text } from '@/components/ui/primitives';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { AppSelect } from '@/components/ui/AppSelect';

// Hooks and Utils
import { useToast } from '@/hooks/useToast';
import { trpc, useTRPCClient } from '@/lib/trpc';
import { isUnauthorizedError } from '@/lib/authUtils';
import AppHeader from '@/components/AppHeader';
import SeasonTemplateBuilder, { type SeasonBuilderResult } from '@/components/SeasonTemplateBuilder';
import { getLeagueIndexHref } from '@/lib/routes';
import { clientLogger } from '@/lib/datadog';

type SeasonMode = 'nfl-pickem' | 'march-madness-player-pool';

const formatCurrency = (value?: string | null) => {
  if (!value) return '$0.00';
  const amount = Number(value);
  if (Number.isNaN(amount)) return '$0.00';
  return `$${amount.toFixed(2)}`;
};

const getTemplateUnitLabelPlural = (metadata?: Record<string, unknown> | null) => {
  const value = metadata?.unitLabelPlural;
  return typeof value === 'string' && value.trim() ? value.trim() : 'Weeks';
};

export default function CreateLeagueScreen() {
  const router = useRouter();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const trpcClient = useTRPCClient();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    weeklyBuyIn: '0',
  });
  const [seasonBuilderOpen, setSeasonBuilderOpen] = useState(false);
  const [seasonDraft, setSeasonDraft] = useState<SeasonBuilderResult | null>(null);
  const [seasonMode, setSeasonMode] = useState<SeasonMode>('nfl-pickem');
  const [marchMadnessConfig, setMarchMadnessConfig] = useState({
    label: `March Madness ${new Date().getFullYear()}`,
    seasonYear: String(new Date().getFullYear()),
    rosterSize: '6',
  });

  const createLeagueMutation = useMutation({
    mutationFn: async (data: any) => {
      const league = await trpcClient.leagues.create.mutate(data);
      if (!league?.id) {
        throw new Error('League response is missing an id.');
      }
      return league;
    },
    onSuccess: async (league) => {
      if (seasonMode === 'march-madness-player-pool' && league?.id && league?.resolvedSeasonId) {
        try {
          await trpcClient.marchMadness.setup.mutate({
            leagueId: league.id,
            seasonId: league.resolvedSeasonId,
            seasonYear: Number.parseInt(marchMadnessConfig.seasonYear, 10),
            rosterSize: Number.parseInt(marchMadnessConfig.rosterSize, 10) || 6,
            gender: 'mens',
          });
        } catch (error) {
          clientLogger.error('Failed to run March Madness setup after league creation', {
            error: error instanceof Error ? error.message : String(error),
            leagueId: league.id,
            seasonId: league.resolvedSeasonId,
          });
          toast.show({
            type: 'error',
            text1: 'Setup Pending',
            text2: 'League created, but March Madness import failed. Retry setup in league settings.',
          });
        }
      }

      toast.show({
        type: 'success',
        text1: 'Success',
        text2: 'League created successfully!',
      });
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      router.replace(getLeagueIndexHref(String(league.id)));
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          type: 'error',
          text1: 'Unauthorized',
          text2: 'You are logged out. Please log in again.',
        });
        // Handle auth redirect
        return;
      }
      toast.show({
        type: 'error',
        text1: 'Error',
        text2: 'Failed to create league. Please try again.',
      });
    },
  });

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.show({
        type: 'error',
        text1: 'Error',
        text2: 'League name is required',
      });
      return;
    }

    if (seasonMode === 'nfl-pickem' && !seasonDraft) {
      toast.show({
        type: 'error',
        text1: 'Season Required',
        text2: 'Configure your first season before creating the league.',
      });
      return;
    }

    if (seasonMode === 'march-madness-player-pool') {
      const seasonYear = Number.parseInt(marchMadnessConfig.seasonYear, 10);
      if (!Number.isInteger(seasonYear) || seasonYear < 2000 || seasonYear > 2200) {
        toast.show({
          type: 'error',
          text1: 'Invalid Year',
          text2: 'Enter a valid tournament year (e.g. 2026).',
        });
        return;
      }

      createLeagueMutation.mutate({
        name: formData.name,
        description: formData.description || null,
        weeklyBuyIn: '0',
        scoringSystem: 'oddsBased',
        seasonConfig: {
          label: marchMadnessConfig.label.trim() || `March Madness ${seasonYear}`,
          mode: 'march-madness-player-pool',
          scoringConfig: {},
          weeklyBuyIn: '0',
          seasonBuyIn: '0',
          isActive: true,
          additionalInfo: {
            sport: 'basketball',
            gender: 'mens',
            seasonYear,
            sourceAdapter: 'henrygd-ncaa-api',
          },
        },
      });
      return;
    }

    const nflSeasonDraft = seasonDraft;
    if (!nflSeasonDraft) {
      toast.show({
        type: 'error',
        text1: 'Season Required',
        text2: 'Configure your first season before creating the league.',
      });
      return;
    }

    const scoringSystem = nflSeasonDraft.scoringSystem ?? 'oddsBased';
    createLeagueMutation.mutate({
      name: formData.name,
      description: formData.description || null,
      weeklyBuyIn: nflSeasonDraft.weeklyBuyIn && nflSeasonDraft.weeklyBuyIn.length > 0 ? nflSeasonDraft.weeklyBuyIn : '0',
      scoringSystem,
      seasonConfig: {
        label: nflSeasonDraft.label,
        mode: 'nfl-pickem',
        templateConfig: nflSeasonDraft.config,
        scoringConfig: { legacyScoringSystem: scoringSystem },
        weeklyBuyIn: nflSeasonDraft.weeklyBuyIn,
        seasonBuyIn: nflSeasonDraft.seasonBuyIn,
        isActive: nflSeasonDraft.isActive,
        additionalInfo: nflSeasonDraft.additionalInfo ?? null,
      },
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSeasonDraftSubmit = async (result: SeasonBuilderResult) => {
    setSeasonDraft(result);
    setFormData((prev) => ({ ...prev, weeklyBuyIn: result.weeklyBuyIn || '0' }));
  };

  return (
    <Box flex={1} backgroundColor="background">
      <AppHeader title="Create League" />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <Card>
          <CardContent style={styles.cardContent}>
            <Box marginBottom="l">
              <Text variant="h2" color="press" marginBottom="s">Create New League</Text>
              <Text variant="bodySmall" color="pressMuted" lineHeight={20}>
                Set up your NFL Pick{"'"}em league and invite friends to compete
              </Text>
            </Box>

            <Box gap="l">
              {/* League Name */}
              <Box gap="s">
                <Label>League Name *</Label>
                <Input
                  placeholder="e.g., Fantasy Football Friends"
                  value={formData.name}
                  onChangeText={(value) => handleInputChange('name', value)}
                  autoCapitalize="words"
                  autoCorrect={false}
                />
              </Box>

              {/* Description */}
              <Box gap="s">
                <Label>Description</Label>
                <Textarea
                  placeholder="Optional description for your league"
                  value={formData.description}
                  onChangeText={(value) => handleInputChange('description', value)}
                  rows={3}
                />
              </Box>

              <Box gap="s">
                <Label>Season Mode</Label>
                <AppSelect
                  value={seasonMode}
                  onChange={(value) => setSeasonMode(value as SeasonMode)}
                  options={[
                    { value: 'nfl-pickem', label: "NFL Pick'em" },
                    { value: 'march-madness-player-pool', label: 'March Madness Player Pool' },
                  ]}
                  placeholder="Select season mode"
                />
              </Box>

              {/* Season Template */}
              {seasonMode === 'nfl-pickem' ? (
                <Box gap="s">
                  <Text variant="body" fontWeight="600" color="text">Create First Season</Text>
                  {seasonDraft ? (
                    <Box 
                      borderRadius="none" 
                      borderWidth={3}
                      borderColor="press"
                      padding="m" 
                      gap="xs"
                      backgroundColor="paperDark"
                    >
                      <Text variant="bodySmall" color="text">
                        Template: {seasonDraft.templateSummary.label}
                      </Text>
                      <Text variant="bodySmall" color="text">Season Label: {seasonDraft.label}</Text>
                      <Text variant="bodySmall" color="text">
                        {getTemplateUnitLabelPlural(seasonDraft.templateSummary.metadata)} Enabled:{' '}
                        {seasonDraft.config.weeks.filter((week) => week.enabled !== false).length}
                      </Text>
                      <Text variant="bodySmall" color="text">
                        Scoring: {seasonDraft.scoringSystem === 'oddsBased' ? 'Odds-Based' : 'Total Wins'}
                      </Text>
                      <Text variant="bodySmall" color="text">
                        Weekly Buy-In:{' '}
                        {formatCurrency(
                          seasonDraft.weeklyBuyIn && seasonDraft.weeklyBuyIn.length
                            ? seasonDraft.weeklyBuyIn
                            : '0',
                        )}
                      </Text>
                      <Text variant="bodySmall" color="text">
                        Season Buy-In:{' '}
                        {formatCurrency(
                          seasonDraft.seasonBuyIn && seasonDraft.seasonBuyIn.length
                            ? seasonDraft.seasonBuyIn
                            : '0',
                        )}
                      </Text>
                      <Text variant="caption" color="pressMuted" marginTop="xs">
                        PCKM tracks earnings only—commissioners still collect and distribute funds.
                      </Text>
                    </Box>
                  ) : (
                    <Text variant="caption" color="pressMuted" lineHeight={20}>
                      Choose a template to pre-load weeks, games, and bets for your league&apos;s opening
                      season.
                    </Text>
                  )}
                  <Button variant="outline" onPress={() => setSeasonBuilderOpen(true)}>
                    <Text variant="button" color="press">
                      {seasonDraft ? 'Edit Season Configuration' : 'Configure Season'}
                    </Text>
                  </Button>
                </Box>
              ) : (
                <Box gap="s">
                  <Text variant="body" fontWeight="600" color="text">March Madness Setup</Text>
                  <Text variant="caption" color="pressMuted" lineHeight={20}>
                    Create a six-player snake draft pool and score by each roster&apos;s top five cumulative player totals across the tournament.
                  </Text>
                  <Box gap="s">
                    <Label>Season Label</Label>
                    <Input
                      placeholder="e.g., March Madness 2026"
                      value={marchMadnessConfig.label}
                      onChangeText={(value) =>
                        setMarchMadnessConfig((prev) => ({ ...prev, label: value }))
                      }
                    />
                  </Box>
                  <Box gap="s">
                    <Label>Tournament Year</Label>
                    <Input
                      placeholder="2026"
                      value={marchMadnessConfig.seasonYear}
                      onChangeText={(value) =>
                        setMarchMadnessConfig((prev) => ({ ...prev, seasonYear: value }))
                      }
                      keyboardType="number-pad"
                    />
                  </Box>
                  <Box gap="s">
                    <Label>Roster Size</Label>
                    <Input
                      placeholder="6"
                      value={marchMadnessConfig.rosterSize}
                      onChangeText={(value) =>
                        setMarchMadnessConfig((prev) => ({ ...prev, rosterSize: value }))
                      }
                      keyboardType="number-pad"
                    />
                  </Box>
                </Box>
              )}

              {/* Submit Button */}
              <Button
                onPress={handleSubmit}
                disabled={createLeagueMutation.isPending}
                marginTop="s"
                fluid
              >
                {createLeagueMutation.isPending ? 'Creating...' : 'Create League'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </ScrollView>
      <SeasonTemplateBuilder
        visible={seasonBuilderOpen}
        onClose={() => setSeasonBuilderOpen(false)}
        onSubmit={handleSeasonDraftSubmit}
        initialState={
          seasonDraft
            ? {
                templateId: seasonDraft.config.templateId,
                label: seasonDraft.label,
                weeks: seasonDraft.config.weeks,
                scoringSystem: seasonDraft.scoringSystem,
                isActive: seasonDraft.isActive,
              }
            : null
        }
        submitLabel="Save Season"
      />
    </Box>
  );
}

const styles = StyleSheet.create(theme => ({
  cardContent: {
    gap: theme.spacing.l,
    paddingTop: theme.spacing.l,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: theme.spacing.m,
    paddingBottom: theme.spacing.xxl,
  },
}));
